import React, {useCallback, useMemo, useState} from 'react';

import {AddedFile} from 'src/components/added-files';
import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {UPLOAD_ACCOUNTS_LIST_TEMPLATE} from 'src/constants';
import {runAccountIdsSearch} from 'src/openapi/admin/apis/RunAccountSearchApi';
import {useAppDispatch} from 'src/store/hooks';

import {Button, FlatButton} from '@pb/kit-component-button';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {DropZone} from '@pb/kit-component-dropzone';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Loading from '@pb/kit-component-loading';
import {Message} from '@pb/kit-component-message';
import {pluralize} from '@pb/kit-formatters';

import {parseAccountsFile, validateAccountsFile} from './acounts-file-parser';
import {TOOL_TITLE as ASSIGN_TOOL_TITLE} from './assign/constants';
import {MAX_RECORDS} from './constants';
import {TOOL_TITLE as DISCHARGE_TOOL_TITLE} from './discharge/constants';

import type {ProcessBulkAction} from 'src/components/bulk-tool/typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {UpdateEntitiesEntitlementsRelation} from 'src/openapi/admin/models/UpdateEntitiesEntitlementsRelation';
import type {ServerResponse} from 'src/typedef';

type Props = {
    ids: string[],
    mode: 'ASSIGN' | 'DISCHARGE',
    onCloseModal: (submit: boolean) => void,
    processAction: ProcessBulkAction<UpdateEntitiesEntitlementsRelation>,
};

const MODAL_WIDTH = 800;

export const UpdateAccountsEntitlementsModal = ({ids, onCloseModal, processAction, mode}: Props) => {
    const apiDispatch = useAppDispatch();

    const [entitiesIds, setEntitiesIds] = useState<ServerResponse<number[]> | null>(null);
    const [addedFile, setAddedFile] = useState<File | null>(null);

    const [fileError, setFileError] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingError, setLoadingError] = useState('');

    const grantIds = useMemo(
        () => [...new Set(ids.map(id => JSON.parse(id.toString()).grantId).filter(Boolean))],
        [ids],
    );

    const roleIds = useMemo(
        () => ids.map(id => JSON.parse(id.toString()).roleId).filter(Boolean),
        [ids],
    );

    const update = useCallback(async (options?: ActionOptions) => {
        if (entitiesIds?.data?.length) {
            return await processAction({
                entitiesIds: entitiesIds.data,
                roleIds: roleIds.length ? roleIds : undefined,
                grantIds: grantIds.length ? grantIds : undefined,
            }, options);
        }
    }, [roleIds, grantIds, entitiesIds, processAction]);

    const onLoadFile = useCallback(async (accepted: File[]) => {
        setEntitiesIds(null);
        setLoadingError('');
        setFileError('');

        let loadedFile: File | null = null;
        try {
            await validateAccountsFile(accepted[0]);
            loadedFile = accepted[0];
        } catch (error) {
            if (error instanceof Error) {
                setEntitiesIds(null);
                setFileError(error.message);
                setAddedFile(null);
                setLoading(false);
            }
        }
        if (loadedFile) {
            parseAccountsFile(loadedFile).then(async parsedData => {
                setLoading(true);

                const accountNames = parsedData
                    .filter(({accountName}) => accountName)
                    .map(({accountName}) => accountName || '');

                const accountIds = parsedData
                    .filter(({accountId}) => accountId)
                    .map(({accountId}) => accountId || 0);

                let uniqueAccountIds: number[] = [];

                if (accountNames.length) {
                    const accountIdsByNames = await apiDispatch(runAccountIdsSearch({
                        accountIdsSearchRequest: {
                            searchCriteria: {
                                accountNames,
                            },
                        },
                    }));
                    if (accountIdsByNames.payload.success) {
                        uniqueAccountIds = [...new Set([...uniqueAccountIds, ...accountIdsByNames.payload.data])];
                    } else {
                        setEntitiesIds({error: accountIdsByNames.payload.data});
                        setAddedFile(null);
                        setLoading(false);
                        return;
                    }
                }

                if (accountIds.length) {
                    const accountIdsByIds = await apiDispatch(runAccountIdsSearch({
                        accountIdsSearchRequest: {
                            searchCriteria: {
                                accountIds,
                            },
                        },
                    }));
                    if (accountIdsByIds.payload.success) {
                        uniqueAccountIds = [...new Set([...uniqueAccountIds, ...accountIdsByIds.payload.data])];
                    } else {
                        setEntitiesIds({error: accountIdsByIds.payload.data});
                        setAddedFile(null);
                        setLoading(false);
                        return;
                    }
                }

                setEntitiesIds({data: uniqueAccountIds});
                setAddedFile(loadedFile);
                setLoading(false);
            }).catch((error: Error) => {
                setFileError(error.message);
                setAddedFile(null);
                setLoading(false);
                setEntitiesIds(null);
            });
        }
    }, [setEntitiesIds, setLoadingError, setFileError, setAddedFile, apiDispatch]);

    const removeFile = useCallback(() => {
        setAddedFile(null);
        setEntitiesIds(null);
    }, [setAddedFile, setEntitiesIds]);

    const downloadTemplate = useCallback(() => {
        window.open(UPLOAD_ACCOUNTS_LIST_TEMPLATE);
    }, []);

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {mode === 'ASSIGN' ? ASSIGN_TOOL_TITLE : DISCHARGE_TOOL_TITLE}
            </DialogHeader>
            <DialogMain style={{padding: 24, maxWidth: MODAL_WIDTH}}>
                {loadingError ? <Message color="error">{loadingError}</Message> : null}
                <Group direction="v">
                    <DropZone multiple={false} onDrop={onLoadFile} mix="dropzone">
                        <div style={{width: 750}}>
                            {addedFile ? (
                                <Group direction="v" gap="s">
                                    <Icon className="dropzone__service-icon" type={Icons.CHECK_CIRCLE}/>
                                    <span>Completed</span>
                                </Group>
                            ) : (
                                <Group direction="v" gap="xxs">
                                    <Icon className="dropzone__service-icon" type={Icons.UPLOAD}/>
                                    <span>Drag & drop a file to upload or <a>browse</a></span>
                                    <span>Max. accounts count: {MAX_RECORDS} records. Accepted formats: CSV</span>
                                    <span>Required columns: <b>Account Name</b> or <b>ID</b></span>
                                </Group>
                            )}
                        </div>
                    </DropZone>
                    {addedFile ? (
                        <Group justify="space-between" alignItems="start">
                            <AddedFile addedFile={addedFile} removeFile={removeFile}/>
                            {loading ? (
                                <Group alignItems="center"><Loading type="ICON"/> Loading...</Group>
                            ) : null}
                            {entitiesIds?.data ? (
                                <b>
                                    {pluralize(`${entitiesIds.data.length} account[s] found`, entitiesIds.data.length)}
                                </b>
                            ) : null}
                        </Group>
                    ) : null}
                    {fileError ? (
                        <Message color="error">{fileError}</Message>
                    ) : null}
                </Group>
            </DialogMain>
            <BulkActionFooter action={mode === 'ASSIGN' ? 'Assign' : 'Unassign'} onConfirm={update} disabled={!entitiesIds?.data?.length}>
                <Group justify="space-between">
                    <Group>

                        <Button variant="Type.SECONDARY" onClick={() => onCloseModal(false)}>
                            Cancel
                        </Button>
                    </Group>
                    <FlatButton onClick={downloadTemplate}>Download the template</FlatButton>
                </Group>
            </BulkActionFooter>
        </>
    );
};
